// SPDX-License-Identifier: AGPL-3.0-or-later

import {Platform} from '@app/features/platform/types/Platform';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import {shouldUseNativeVoiceEngine} from '@app/features/voice/engine/native_voice_engine/NativeVoiceEngineSelection';
import type {VoiceEngineAudioInputDevice} from '@app/features/voice/engine/native_voice_engine/VoiceEngine';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';

const logger = new Logger('VoiceDeviceManager');

export function resolveEffectiveDeviceId(
	storedDeviceId: string,
	devices: ReadonlyArray<MediaDeviceInfo>,
): string | null {
	if (devices.length === 0) {
		return null;
	}
	const deviceExists = devices.some((d) => d.deviceId === storedDeviceId);
	if (deviceExists) {
		return storedDeviceId;
	}
	return devices[0].deviceId;
}

export function hasDeviceLabels(devices: ReadonlyArray<MediaDeviceInfo>): boolean {
	return devices.some((d) => d.label && d.label.trim().length > 0);
}

type PermissionStatus = 'idle' | 'loading' | 'granted' | 'denied';

export interface EnsureVoiceDevicesOptions {
	requestPermissions?: boolean;
	forceRefresh?: boolean;
}

export interface VoiceDeviceState {
	inputDevices: Array<MediaDeviceInfo>;
	outputDevices: Array<MediaDeviceInfo>;
	videoDevices: Array<MediaDeviceInfo>;
	permissionStatus: PermissionStatus;
}

type Listener = (state: VoiceDeviceState) => void;

const INTERNAL_VIRTUAL_AUDIO_DEVICE_LABELS = [
	'fluxer-screen-share',
	'fluxer screen share',
	'fluxer-direct-capture',
	'fluxer direct capture',
];

export function isInternalVirtualAudioDevice(device: MediaDeviceInfo): boolean {
	const label = device.label.trim().toLowerCase();
	return INTERNAL_VIRTUAL_AUDIO_DEVICE_LABELS.some((internalLabel) => label.includes(internalLabel));
}

const sortDevices = (devices: Array<MediaDeviceInfo>): Array<MediaDeviceInfo> => {
	return [...devices].sort((a, b) => {
		const aIsDefault = a.deviceId === 'default' || (a as {isDefault?: boolean}).isDefault === true;
		const bIsDefault = b.deviceId === 'default' || (b as {isDefault?: boolean}).isDefault === true;
		if (aIsDefault && !bIsDefault) return -1;
		if (!aIsDefault && bIsDefault) return 1;
		return a.label.localeCompare(b.label);
	});
};

type NativeInputMediaDeviceInfo = MediaDeviceInfo & {isDefault?: boolean};

type NativeVoiceInputBridge = {
	listAudioInputDevices?: () => Promise<Array<VoiceEngineAudioInputDevice>>;
};

function getNativeVoiceInputBridge(): NativeVoiceInputBridge | null {
	if (typeof window === 'undefined') return null;
	return window.electron?.voiceEngine ?? null;
}

function nativeInputDeviceToMediaDeviceInfo(device: VoiceEngineAudioInputDevice): NativeInputMediaDeviceInfo {
	const deviceId = device.deviceId.trim();
	const label = device.label.trim();
	const kind: MediaDeviceKind = 'audioinput';
	const groupId = '';
	return {
		deviceId,
		groupId,
		kind,
		label,
		isDefault: device.isDefault,
		toJSON: () => ({deviceId, groupId, kind, label}),
	} as NativeInputMediaDeviceInfo;
}

export function shapeNativeAudioInputDevices(
	devices: ReadonlyArray<VoiceEngineAudioInputDevice>,
): Array<MediaDeviceInfo> {
	return sortDevices(
		devices
			.filter((device) => device.deviceId.trim().length > 0)
			.map((device) => nativeInputDeviceToMediaDeviceInfo(device)),
	);
}

class VoiceDeviceManager {
	private state: VoiceDeviceState = {
		inputDevices: [],
		outputDevices: [],
		videoDevices: [],
		permissionStatus: 'idle',
	};
	private listeners = new Set<Listener>();
	private enumeratingPromise: Promise<VoiceDeviceState> | null = null;
	private queuedPermissionEnumerationPromise: Promise<VoiceDeviceState> | null = null;
	private currentEnumerationRequestsPermissions = false;
	private shouldRequestPermissions = false;
	private hasEnumeratedDevices = false;
	private lastEnumerationUsedNativeInputs: boolean | null = null;

	constructor() {
		if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
			navigator.mediaDevices.addEventListener('devicechange', this.handleDeviceChange);
		}
	}

	public getState(): VoiceDeviceState {
		return this.state;
	}

	public subscribe(listener: Listener): () => void {
		this.listeners.add(listener);
		listener(this.state);
		return () => {
			this.listeners.delete(listener);
		};
	}

	public async ensureDevices(options: EnsureVoiceDevicesOptions = {}): Promise<VoiceDeviceState> {
		const requestPermissions = options.requestPermissions ?? false;
		const forceRefresh = options.forceRefresh ?? false;
		logger.debug('ensureDevices called', {
			requestPermissions,
			forceRefresh,
			shouldRequestPermissions: this.shouldRequestPermissions,
			hasEnumeratingPromise: !!this.enumeratingPromise,
			currentState: {
				inputDeviceCount: this.state.inputDevices.length,
				permissionStatus: this.state.permissionStatus,
			},
		});
		if (requestPermissions) {
			this.shouldRequestPermissions = true;
		}
		const shouldRequest = this.shouldRequestPermissions || requestPermissions;
		const useNativeInputDevices = await this.shouldUseNativeInputDevices();
		if (!forceRefresh && !this.enumeratingPromise && this.canUseCachedState(shouldRequest, useNativeInputDevices)) {
			logger.debug('Using cached device state');
			return this.state;
		}
		if (this.enumeratingPromise) {
			if (shouldRequest && !this.currentEnumerationRequestsPermissions) {
				logger.debug('Queueing permissioned enumeration after current enumeration');
				if (!this.queuedPermissionEnumerationPromise) {
					this.queuedPermissionEnumerationPromise = this.enumeratingPromise
						.catch(() => this.state)
						.then(async () => this.startEnumeration(true, await this.shouldUseNativeInputDevices()))
						.finally(() => {
							this.queuedPermissionEnumerationPromise = null;
						});
				}
				return this.queuedPermissionEnumerationPromise;
			}
			logger.debug('Joining existing enumeration promise');
			return this.enumeratingPromise;
		}
		logger.debug('Creating new enumeration promise');
		return this.startEnumeration(shouldRequest, useNativeInputDevices);
	}

	private canUseCachedState(requestPermissions: boolean, useNativeInputDevices: boolean): boolean {
		if (!this.hasEnumeratedDevices) {
			return false;
		}
		if (this.lastEnumerationUsedNativeInputs !== useNativeInputDevices) {
			return false;
		}
		if (!requestPermissions) {
			return true;
		}
		return this.state.permissionStatus === 'granted';
	}

	private startEnumeration(requestPermissions: boolean, useNativeInputDevices: boolean): Promise<VoiceDeviceState> {
		this.currentEnumerationRequestsPermissions = requestPermissions;
		const pendingPromise = this.enumerateDevices(requestPermissions, useNativeInputDevices).catch((error) => {
			logger.debug('Failed to enumerate media devices:', error);
			throw error;
		});
		this.enumeratingPromise = pendingPromise;
		return pendingPromise.finally(() => {
			if (this.enumeratingPromise === pendingPromise) {
				logger.debug('Enumeration promise completed');
				this.enumeratingPromise = null;
				this.currentEnumerationRequestsPermissions = false;
			}
		});
	}

	private async enumerateDevices(
		requestPermissions: boolean,
		useNativeInputDevices: boolean,
	): Promise<VoiceDeviceState> {
		logger.debug('enumerateDevices started', {requestPermissions});
		if (!navigator.mediaDevices?.enumerateDevices) {
			logger.debug('Navigator or mediaDevices API not available');
			return this.state;
		}
		if (requestPermissions && this.state.permissionStatus !== 'granted') {
			logger.debug('Setting permission status to loading');
			this.updateState({permissionStatus: 'loading'});
		}
		try {
			logger.debug('Calling navigator.mediaDevices.enumerateDevices()');
			let devices = await navigator.mediaDevices.enumerateDevices();
			let permissionStatus = this.state.permissionStatus;
			logger.debug('Initial enumeration result', {
				deviceCount: devices.length,
				devices: devices.map((d) => ({
					kind: d.kind,
					deviceId: d.deviceId,
					label: d.label,
					hasLabel: !!d.label,
				})),
			});
			const hasLabels = devices.some((device) => device.label && device.label !== '');
			let usedNativeFlow = false;
			if (hasLabels) {
				logger.debug('Devices have labels, permissions already granted');
				permissionStatus = 'granted';
			} else if (requestPermissions && isDesktop()) {
				logger.debug('No labels detected; attempting native permission flow');
				const [nativeMic, nativeCamera] = await Promise.all([
					ensureNativePermission('microphone'),
					ensureNativePermission('camera'),
				]);
				usedNativeFlow = nativeMic !== 'unsupported' || nativeCamera !== 'unsupported';
				if (nativeMic === 'denied' || nativeCamera === 'denied') {
					permissionStatus = 'denied';
				} else if (nativeMic === 'granted' || nativeCamera === 'granted') {
					permissionStatus = 'granted';
				}
			}
			if (!hasLabels && requestPermissions && (!usedNativeFlow || permissionStatus !== 'granted')) {
				const isIOSPWA = Platform.isIOSWeb && Platform.isPWA;
				let skipGetUserMedia = false;
				if (isIOSPWA && navigator.permissions) {
					try {
						const micPermission = await navigator.permissions.query({name: 'microphone' as PermissionName});
						if (micPermission.state === 'granted') {
							logger.debug('iOS PWA: microphone permission already granted via Permissions API, skipping getUserMedia');
							permissionStatus = 'granted';
							devices = await navigator.mediaDevices.enumerateDevices();
							skipGetUserMedia = devices.some((d) => d.label && d.label !== '');
						}
					} catch {}
				}
				if (!skipGetUserMedia) {
					logger.debug('No labels found, requesting permissions via getUserMedia');
					try {
						const stream = await navigator.mediaDevices.getUserMedia({
							audio: !useNativeInputDevices,
							video: true,
						});
						logger.debug('getUserMedia succeeded, stopping tracks');
						stream.getTracks().forEach((track) => {
							logger.debug('Stopping track', {kind: track.kind, label: track.label});
							track.stop();
						});
						permissionStatus = 'granted';
						logger.debug('Re-enumerating devices after permission grant');
						devices = await navigator.mediaDevices.enumerateDevices();
						logger.debug('Re-enumeration result', {
							deviceCount: devices.length,
							devices: devices.map((d) => ({
								kind: d.kind,
								deviceId: d.deviceId,
								label: d.label,
							})),
						});
					} catch (error) {
						logger.debug('getUserMedia failed', {
							error,
							errorName: error instanceof DOMException ? error.name : 'unknown',
							errorMessage: error instanceof Error ? error.message : String(error),
						});
						if (
							error instanceof DOMException &&
							(error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
						) {
							permissionStatus = 'denied';
						} else {
							permissionStatus = 'granted';
						}
					}
				}
			}
			const browserInputDevices = sortDevices(
				devices.filter((device) => device.kind === 'audioinput' && !isInternalVirtualAudioDevice(device)),
			);
			const nativeInputDevices = await this.enumerateNativeInputDevices(useNativeInputDevices);
			const inputDevices = nativeInputDevices ?? browserInputDevices;
			const outputDevices = sortDevices(
				devices.filter((device) => device.kind === 'audiooutput' && !isInternalVirtualAudioDevice(device)),
			);
			const videoDevices = sortDevices(devices.filter((device) => device.kind === 'videoinput'));
			const nextState: VoiceDeviceState = {
				inputDevices,
				outputDevices,
				videoDevices,
				permissionStatus: this.resolvePermissionStatus(requestPermissions, permissionStatus),
			};
			this.hasEnumeratedDevices = true;
			this.lastEnumerationUsedNativeInputs = useNativeInputDevices;
			logger.debug('Final device state', {
				inputDeviceCount: inputDevices.length,
				outputDeviceCount: outputDevices.length,
				videoDeviceCount: videoDevices.length,
				permissionStatus: nextState.permissionStatus,
			});
			this.updateState(nextState);
			return this.state;
		} catch (_error) {
			logger.debug('enumerateDevices failed with exception', _error);
			if (requestPermissions) {
				this.updateState({permissionStatus: 'denied'});
			}
			return this.state;
		}
	}

	private async shouldUseNativeInputDevices(): Promise<boolean> {
		try {
			return await shouldUseNativeVoiceEngine();
		} catch (error) {
			logger.warn('Failed to resolve native voice engine selection for input devices', {error});
			return false;
		}
	}

	private async enumerateNativeInputDevices(useNativeInputDevices: boolean): Promise<Array<MediaDeviceInfo> | null> {
		if (!useNativeInputDevices) {
			return null;
		}
		const bridge = getNativeVoiceInputBridge();
		if (!bridge?.listAudioInputDevices) {
			return null;
		}
		try {
			const nativeDevices = shapeNativeAudioInputDevices(await bridge.listAudioInputDevices());
			if (nativeDevices.length === 0) {
				return null;
			}
			return nativeDevices;
		} catch (error) {
			logger.warn('Failed to enumerate native audio input devices', {error});
			return null;
		}
	}

	private resolvePermissionStatus(requestPermissions: boolean, computedStatus: PermissionStatus): PermissionStatus {
		if (!requestPermissions) {
			if (this.state.permissionStatus === 'denied') {
				return 'denied';
			}
			if (this.state.permissionStatus === 'granted') {
				return 'granted';
			}
		}
		return computedStatus;
	}

	private updateState(partial: Partial<VoiceDeviceState>) {
		this.state = {
			...this.state,
			...partial,
		};
		this.listeners.forEach((listener) => listener(this.state));
	}

	private handleDeviceChange = () => {
		this.hasEnumeratedDevices = false;
		void this.ensureDevices({requestPermissions: this.shouldRequestPermissions});
	};
}

export const voiceDeviceManager = new VoiceDeviceManager();
